import { Panel } from '@insify/design-system/components';
import { Employees } from '@insify/design-system/line-icons';
import { Revenue, useWizard } from '@insify/funnel-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCommands } from '../../hooks/useCommands';
import { useContent } from '../../hooks/useContent';
import { useConditionsQuery, useHighCareConditions, useParamsQuery } from '../../hooks/useQueries';

import type { RevenueFormType } from '@insify/funnel-components';
import type { SubmitHandler, UseFormReturn } from 'react-hook-form';

type RevenueWithThresholdSubcontractors = RevenueFormType & {
  activitySpecificFieldAnswers?: {
    thresholdSubcontractors20: boolean;
    thresholdSubcontractors40: boolean;
  };
};

export function RevenuePage(): JSX.Element | null {
  const { showPreviousPage, showNextPage } = useWizard();
  const { t } = useTranslation(['common', 'validation']);
  const { quoteWizardRevenue } = useContent() || {};
  const { sendCommand } = useCommands();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const { isStale: paramsStale, data = {} } = useParamsQuery();
  const { data: quoteConditions } = useConditionsQuery();
  const { isStale: highCareConditionsStale } = useHighCareConditions();

  const subContractor20 = quoteConditions?.activitySpecificFieldDescriptions?.find(
    ({ key }) => key === 'thresholdSubcontractors20',
  );

  const subContractor40 = quoteConditions?.activitySpecificFieldDescriptions?.find(
    ({ key }) => key === 'thresholdSubcontractors40',
  );

  const subContractor = subContractor20 ?? subContractor40;

  const handleCommand: SubmitHandler<RevenueWithThresholdSubcontractors> = async ({
    annualRevenue,
    activitySpecificFieldAnswers,
  }) => {
    setIsSubmitting(true);

    await sendCommand({
      type: 'enterAnnualRevenue',
      payload: {
        annualRevenue: {
          cents: annualRevenue.cents.toString(),
          currency: 'EUR',
        },
      },
    });

    if (subContractor && activitySpecificFieldAnswers) {
      await sendCommand({
        type: 'confirmActivitySpecificFields',
        payload: activitySpecificFieldAnswers,
      });
    }

    setTriggerShowingNextPage(true);
  };

  const hasCompletedRevenueStep = Boolean(
    triggerShowingNextPage && !paramsStale && data?.annualRevenue && !highCareConditionsStale,
  );
  useEffect(() => {
    if (hasCompletedRevenueStep) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [hasCompletedRevenueStep]);

  const renderThresholdSubcontractors = ({
    register,
  }: UseFormReturn<RevenueWithThresholdSubcontractors>): JSX.Element | null =>
    subContractor ? (
      <Panel
        id={`activitySpecificFieldAnswers.${subContractor.key}`}
        icon={<Employees />}
        trackingId='subContractor'
        aria-label={subContractor.text}
        disabled={isSubmitting}
        {...register(
          `activitySpecificFieldAnswers.${
            subContractor.key as 'thresholdSubcontractors20' | 'thresholdSubcontractors40'
          }`,
        )}
        description={<span dangerouslySetInnerHTML={{ __html: subContractor.text }} />}
      />
    ) : null;

  return (
    <Revenue
      formData={data}
      eventHandlers={{
        onSubmit: handleCommand,
        onBack: showPreviousPage,
      }}
      content={quoteWizardRevenue}
      translations={{
        back: t('common:back'),
        forward: t('common:continue'),
        currency: t('common:currency'),
        validation: {
          type: t('validation:fill_in_one_if_you_do_not_expect_to_have_revenue'),
          required: t('validation:annual_revenue_required'),
          min: t('validation:fill_in_one_if_you_do_not_expect_to_have_revenue'),
          max: t('validation:whoah_maybe_you_should_insure_us'),
        },
      }}
      renderExtraBlock={renderThresholdSubcontractors}
      submitting={isSubmitting}
      thousandSeparator=' '
      currencyPosition='after'
    />
  );
}
